// A shell script read as the commands it runs.
//
// A tool call's command is often a script, not one command: a Codex code-mode
// cell's exec_command carries several lines (`set -e`, an assignment from
// `$(...)`, `VAR=value program args`, a loop), and each program in it starts a
// process of its own that the endpoint records by its own command line. Read
// as one line, the script names no program at all. This module splits it the
// way the shell does and reduces each part to the command the OS starts.
//
// A static reading, never an evaluation: what a variable holds is not known
// here, and a construct it does not model (a `case` arm, a function body read
// out of order) costs a command it could have named, never one the script
// does not run.

// Reading stops here: a call's command is model-authored and can be
// arbitrarily long, and this runs for every shell call on the render path.
const MAX_SCRIPT_CHARS = 20_000;
const MAX_SCRIPT_COMMANDS = 200;
const MAX_SUBSTITUTION_DEPTH = 3;

// The index of the parenthesis closing a `$(`, `<(` or `>(` group whose body
// starts at `start`, or -1 when it never closes.
function groupEnd(text, start) {
    let depth = 1;
    let quote = null;
    for (let i = start; i < text.length; i += 1) {
        const ch = text[i];
        if (quote === "'") {
            if (ch === "'") quote = null;
            continue;
        }
        if (ch === "\\") {
            i += 1;
            continue;
        }
        if (quote === '"') {
            if (ch === '"') quote = null;
            continue;
        }
        if (ch === "'" || ch === '"') quote = ch;
        else if (ch === "(") depth += 1;
        else if (ch === ")") {
            depth -= 1;
            if (depth === 0) return i;
        }
    }
    return -1;
}

// The here-document delimiter that follows `<<` at `i`: the word after an
// optional `-`, without its quotes, and where the operator ends.
function heredocAt(text, i) {
    const m = /^-?[ \t]*(?:'([^'\n]+)'|"([^"\n]+)"|\\?([A-Za-z0-9_.-]+))/.exec(text.slice(i, i + 200));
    return m ? { delimiter: m[1] ?? m[2] ?? m[3], end: i + m[0].length } : null;
}

/**
 * The simple commands a script runs, in the order it states them: one per
 * line and per `;`, `&&`, `||`, `|` or `&` outside quotes, with line
 * continuations joined, comments and here-document bodies skipped, and the
 * commands inside `$(...)`, `<(...)`, `>(...)` and backticks read as commands
 * of their own (after the commands of the script itself).
 */
export function scriptCommands(script, depth = 0) {
    const src = String(script || "");
    const text = src.length > MAX_SCRIPT_CHARS ? src.slice(0, MAX_SCRIPT_CHARS) : src;
    const out = [];
    const inner = [];
    const heredocs = [];
    let cur = "";
    let quote = null;
    const flush = () => {
        const part = cur.trim();
        if (part && out.length < MAX_SCRIPT_COMMANDS) out.push(part);
        cur = "";
    };
    const substitute = (body) => {
        if (depth < MAX_SUBSTITUTION_DEPTH) inner.push(...scriptCommands(body, depth + 1));
    };
    // A substitution keeps its text in the command around it (it is one of
    // that command's words) and adds its body's commands to the reading.
    const group = (i, bodyStart) => {
        const end = groupEnd(text, bodyStart);
        if (end < 0) return -1;
        substitute(text.slice(bodyStart, end));
        cur += text.slice(i, end + 1);
        return end + 1;
    };
    let i = 0;
    while (i < text.length && out.length < MAX_SCRIPT_COMMANDS) {
        const ch = text[i];
        const next = text[i + 1];
        if (quote === "'") {
            cur += ch;
            if (ch === "'") quote = null;
            i += 1;
            continue;
        }
        if (ch === "\\") {
            cur += next === "\n" ? " " : ch + (next ?? "");
            i += 2;
            continue;
        }
        if (ch === "$" && next === "(") {
            // `$((...))` is arithmetic: no command, and a `<<` inside it is a
            // shift, not a here-document.
            if (text[i + 2] === "(") {
                const end = groupEnd(text, i + 2);
                if (end > 0) {
                    cur += text.slice(i, end + 1);
                    i = end + 1;
                    continue;
                }
            } else {
                const after = group(i, i + 2);
                if (after > 0) {
                    i = after;
                    continue;
                }
            }
        }
        if (ch === "`") {
            const end = text.indexOf("`", i + 1);
            if (end > 0 && !text.slice(i + 1, end).includes("\n")) {
                substitute(text.slice(i + 1, end));
                cur += text.slice(i, end + 1);
                i = end + 1;
                continue;
            }
        }
        if (quote === '"') {
            cur += ch;
            if (ch === '"') quote = null;
            i += 1;
            continue;
        }
        if (ch === "'" || ch === '"') {
            quote = ch;
            cur += ch;
            i += 1;
            continue;
        }
        if ((ch === "<" || ch === ">") && next === "(") {
            const after = group(i, i + 2);
            if (after > 0) {
                i = after;
                continue;
            }
        }
        // A comment starts at a word; `#` inside one (`a#b`, a URL's fragment)
        // is text.
        if (ch === "#" && (!cur || /\s$/.test(cur))) {
            while (i < text.length && text[i] !== "\n") i += 1;
            continue;
        }
        // `<<<` is a here-string, whose word is on the line itself.
        if (ch === "<" && next === "<" && text[i + 2] === "<") {
            cur += "<<<";
            i += 3;
            continue;
        }
        if (ch === "<" && next === "<") {
            const doc = heredocAt(text, i + 2);
            if (doc) {
                heredocs.push(doc.delimiter);
                cur += text.slice(i, doc.end);
                i = doc.end;
                continue;
            }
        }
        if (ch === "\n") {
            flush();
            i += 1;
            // A here-document's body is its command's input, not commands.
            for (const delimiter of heredocs.splice(0)) {
                while (i < text.length) {
                    const eol = text.indexOf("\n", i);
                    const line = text.slice(i, eol < 0 ? text.length : eol);
                    i = eol < 0 ? text.length : eol + 1;
                    if (line.trim() === delimiter) break;
                }
            }
            continue;
        }
        if (ch === ";" || ((ch === "&" || ch === "|") && next === ch)) {
            flush();
            i += ch === ";" ? 1 : 2;
            continue;
        }
        if (ch === "|") {
            flush();
            i += next === "&" ? 2 : 1;
            continue;
        }
        // `&` ends a command it sends to the background; in `2>&1` or `&>` it
        // is part of a redirection.
        if (ch === "&" && !/[<>]\s*$/.test(cur) && next !== ">") {
            flush();
            i += 1;
            continue;
        }
        cur += ch;
        i += 1;
    }
    flush();
    return [...out, ...inner].slice(0, MAX_SCRIPT_COMMANDS);
}

// Words that stand before a command without being one: the shell's reserved
// words, the builtins that run the command after them, and `env`, which execs
// it. The program the OS starts is what follows.
const COMMAND_PREFIXES = new Set([
    "if", "then", "else", "elif", "do", "while", "until", "time", "!",
    "exec", "command", "builtin", "env", "{", "(",
]);
// A part led by one of these runs no command of its own (`for i in 1 2 3`).
const COMMANDLESS_PARTS = new Set(["for", "case", "select", "function"]);
const ASSIGNMENT_RE = /^[A-Za-z_][A-Za-z0-9_]*(\[[^\]]*\])?\+?=/;

// Where the shell word starting at `i` ends, quotes and `$(...)` included.
function wordEnd(part, i) {
    let quote = null;
    for (; i < part.length; i += 1) {
        const ch = part[i];
        if (quote === "'") {
            if (ch === "'") quote = null;
            continue;
        }
        if (ch === "\\") {
            i += 1;
            continue;
        }
        if (quote === '"') {
            if (ch === '"') quote = null;
            continue;
        }
        if (ch === "'" || ch === '"') quote = ch;
        else if (ch === "$" && part[i + 1] === "(") {
            const end = groupEnd(part, i + 2);
            if (end > 0) i = end;
        } else if (ch === "`") {
            const end = part.indexOf("`", i + 1);
            if (end > 0) i = end;
        } else if (/\s/.test(ch)) return i;
    }
    return part.length;
}

/**
 * The command proper of one simple command: the text from the program the OS
 * starts, past the reserved words, the `VAR=value` assignments the shell
 * applies to it and the words that run what follows. Null when the part runs
 * no command (a bare assignment, `for i in ...`).
 */
export function commandProper(part) {
    let text = String(part || "").trim().replace(/^[({!\s]+/, "");
    // `(cd /repo && make)` splits into `(cd /repo` and `make)`: the group's
    // closing mark belongs to no command.
    const opens = (text.match(/[({]/g) || []).length;
    const closes = (text.match(/[)}]/g) || []).length;
    if (closes > opens) text = text.replace(/[\s)}]+$/, "");
    let i = 0;
    let afterEnv = false;
    while (i < text.length) {
        while (i < text.length && /\s/.test(text[i])) i += 1;
        if (i >= text.length) return null;
        const end = wordEnd(text, i);
        const word = text.slice(i, end);
        if (COMMANDLESS_PARTS.has(word)) return null;
        const skip = COMMAND_PREFIXES.has(word) || ASSIGNMENT_RE.test(word) || (afterEnv && word.startsWith("-"));
        if (!skip) return text.slice(i);
        afterEnv = afterEnv || word === "env";
        i = end;
    }
    return null;
}
